import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography, Button, IconButton, Slide, List, ListItemButton, Collapse, Fade, MenuItem, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import logoWhite from '../../assets/languify_logo_white.svg';
import logoBlack from '../../assets/languify_logo_black.svg';

const INTERVIEW_URL = process.env.REACT_APP_INTERVIEW_URL;
const BLOGS_URL = process.env.REACT_APP_BLOGS_URL;

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    width: '100%',
    height: '128px',
    background: 'linear-gradient(180deg, #0A3F63 0%, #0A3F63 100%)',
    padding: theme.spacing(0, 37.5),
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(0, 15),
      height: '104px',
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(0, 15),
      height: '104px',
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 10),
      height: '80px',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      backgroundColor: `linear-gradient(180deg, #0A3F63 0%, #0A3F63 100%)`,
      background: 'linear-gradient(180deg, #0A3F63 0%, #0A3F63 100%)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 5),
    },
  },
  headerContent: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  logoImage: {
    height: '40px',
    [theme.breakpoints.down('lg')]: {
      height: '36px',
    },
    [theme.breakpoints.down('md')]: {
      height: '32px',
    },
  },
  logoImageDesktop: {
    display: 'block',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  logoImageMobile: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  navMenu: {
    display: 'flex',
    gap: theme.spacing(7),
    alignItems: 'center',
    [theme.breakpoints.down('xl')]: {
      gap: theme.spacing(5),
    },
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(3),
    },
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  navItem: {
    ...theme.typography['body01-medium'],
    color: theme.palette.shades['clr-white-900'],
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    transition: 'opacity 0.3s ease',
    padding: theme.spacing(0),
    textTransform: 'none',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      opacity: 0.8,
    },
  },
  navItemText: {
    ...theme.typography['body01-medium'],
    color: theme.palette.shades['clr-white-900'],
  },
  dropdownButton: {
    ...theme.typography['body01-medium'],
    color: theme.palette.shades['clr-white-900'],
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    transition: 'opacity 0.3s ease',
    padding: theme.spacing(0),
    textTransform: 'none',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      opacity: 0.8,
    },
  },
  downArrow: {
    fontSize: '20px',
    color: theme.palette.shades['clr-white-900'],
  },
  menuContainer: {
    position: 'absolute',
    minWidth: '300px',
    backgroundColor: theme.palette.shades['clr-white-900'],
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.16)',
    borderRadius: 4,
    zIndex: 1000,
    marginTop: theme.spacing(3),
  },
  menuItem: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    '&:hover': {
      backgroundColor: theme.palette.primary['clr-50'],
    },
  },
  menuItemText: {
    ...theme.typography['h6-medium'],
    color: theme.palette.shades['clr-black-900'],
  },
  divider: {
    margin: theme.spacing(0),
    backgroundColor: theme.palette.shades['clr-black-900'],
  },
  navButtons: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  navButton: {
    ...theme.typography['body01-medium'],
    color: theme.palette.shades['clr-white-900'],
    border: `1px solid ${theme.palette.shades['clr-white-900']}`,
    borderRadius: '4px',
    padding: theme.spacing(3, 5),
    textTransform: 'none',
    whiteSpace: 'nowrap',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: `1px solid ${theme.palette.shades['clr-white-900']}`,
    },
  },
  navButtonPrimary: {
    backgroundColor: theme.palette.shades['clr-white-900'],
    color: '#0A3F63',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      border: `1px solid ${theme.palette.shades['clr-white-900']}`,
    },
  },
  hamburgerButton: {
    display: 'none',
    color: theme.palette.shades['clr-white-900'],
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  sidebarWrapper: {
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100%',
    width: '100%',
    zIndex: 1300,
  },
  sidebar: {
    position: 'fixed',
    width: '100%',
    height: '100vh',
    top: 0,
    right: 0,
    padding: theme.spacing(7, 5, 4, 5),
    backgroundColor: theme.palette.shades['clr-white-900'],
    boxShadow: '-0px 0 12px grey',
    zIndex: 100,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(5, 4),
    },
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '50px',
  },
  sidebarLogoContainer: {
    cursor: 'pointer',
  },
  sidebarLogo: {
    width: '140px',
    [theme.breakpoints.down('sm')]: {
      width: '110px',
    },
  },
  closeIcon: {
    cursor: 'pointer',
    color: theme.palette.neutral['clr-900'],
  },
  listContainer: {
    width: '100%',
    marginTop: theme.spacing(2.5),
  },
  boxContainer: {
    padding: theme.spacing(1.5, 0),
  },
  listItem: {
    paddingLeft: theme.spacing(1),
  },
  listItemButton: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(8),
    '&.active': {
      color: '#1961BF',
    },
  },
  expandIcon: {
    color: '#1961BF',
  },
  normalExpandIcon: {
    color: 'inherit',
  },
  sidebarButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
  sidebarButton: {
    ...theme.typography['body01-medium'],
    color: theme.palette.primary.main,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: '4px',
    padding: theme.spacing(1.5, 4),
    textTransform: 'none',
    width: '156px',
    height: '48px',
    '&:hover': {
      backgroundColor: 'rgba(25, 97, 191, 0.04)',
    },
  },
  dropdownIcon: {
    fontSize: '20px',
  },
}));

// Menu data
const productData = [
  { id: 'teach&Train', label: 'Teach & Train', link: '/teach&train' },
  { id: 'inprep', label: 'In-prep pathway', link: '/inprep' },
  { id: 'caseMaster', label: 'Case Master AI', link: '/' },
];

const useCaseData = [
  { id: 'education', label: 'Education', link: '/usecase/education' },
  { id: 'interview', label: 'Interview Preparation', link: '/usecase/interview-preparation' },
];

const menuItems = [
  { title: 'Get In Touch', link: process.env.REACT_APP_GET_IN_TOUCH_TALLY_FORM_URL, newPage: true },
];

// Case Master specific header links.
// 'scroll' items jump to a section on this page; 'external' items open a new tab.
const navLinks = [
  { id: 'pricing', label: 'Pricing', action: 'scroll', value: 'pricing' },
  { id: 'faq', label: 'FAQs', action: 'scroll', value: 'faqs' },
  { id: 'blogs', label: 'Blogs', action: 'external', value: BLOGS_URL },
];

// Dropdown Component
const NavDropdown = ({ items, title }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();
  const navigate = useNavigate();

  const handleMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = (event) => {
    const { relatedTarget } = event;
    if (!event.currentTarget.contains(relatedTarget)) {
      setAnchorEl(null);
    }
  };

  const handleMenuItemClick = (link) => {
    setAnchorEl(null);
    navigate(link);
  };

  return (
    <Box position="relative" display="inline-block">
      <Button
        className={classes.dropdownButton}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Typography className={classes.navItemText}>{title}</Typography>
        <KeyboardArrowDownIcon className={classes.downArrow} />
      </Button>
      <Fade in={Boolean(anchorEl)} timeout={300}>
        <Box
          className={classes.menuContainer}
          onMouseEnter={() => setAnchorEl(true)}
          onMouseLeave={handleMouseLeave}
        >
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              <MenuItem onClick={() => handleMenuItemClick(item.link)} className={classes.menuItem}>
                <Typography className={classes.menuItemText}>{item.label}</Typography>
              </MenuItem>
              {index < items.length - 1 && <Divider className={classes.divider} />}
            </React.Fragment>
          ))}
        </Box>
      </Fade>
    </Box>
  );
};

const CaseHeader = () => {
  const classes = useStyles();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openProducts, setOpenProducts] = useState(false);
  const [openUseCase, setOpenUseCase] = useState(false);
  const navigate = useNavigate();

  const handleMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleCloseSidebar = () => {
    setMobileMenuOpen(false);
  };

  const handleNavigate = (link, newPage) => {
    if (newPage) {
      window.open(link, '_blank');
    } else {
      navigate(link);
      handleCloseSidebar();
    }
  };

  const handleNavLink = (item) => {
    if (item.action === 'scroll') {
      const section = document.getElementById(item.value);
      if (section) section.scrollIntoView({ behavior: 'smooth' });
    } else if (item.action === 'external' && item.value) {
      window.open(item.value, '_blank', 'noopener,noreferrer');
    }
    handleCloseSidebar();
  };

  // Existing Case Master users log in via the interview portal.
  const handleLogin = () => {
    if (INTERVIEW_URL) window.open(INTERVIEW_URL, '_blank', 'noopener,noreferrer');
    handleCloseSidebar();
  };

  const handleForAcademies = () => {
    navigate('/business');
    handleCloseSidebar();
  };

  return (
    <>
      <Box className={classes.headerContainer}>
        <Box className={classes.headerContent}>
          {/* Logo */}
          <Box className={classes.logoContainer}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <img src={logoWhite} alt="Languify Logo" className={`${classes.logoImage} ${classes.logoImageDesktop}`} />
              <img src={logoWhite} alt="Languify Logo" className={`${classes.logoImage} ${classes.logoImageMobile}`} />
            </Link>
          </Box>

          {/* Desktop Navigation Menu */}
          <Box className={classes.navMenu}>
            <NavDropdown items={productData} title="Products" />
            <NavDropdown items={useCaseData} title="Solutions" />

            {navLinks.map((item) => (
              <Button
                key={item.id}
                className={classes.navItem}
                onClick={() => handleNavLink(item)}
              >
                <Typography className={classes.navItemText}>{item.label}</Typography>
              </Button>
            ))}

            {menuItems.map((item, index) => (
              item.newPage ? (
                <a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Button className={classes.navItem}>
                    <Typography className={classes.navItemText}>{item.title}</Typography>
                  </Button>
                </a>
              ) : (
                <Link key={index} to={item.link} style={{ textDecoration: 'none' }}>
                  <Button className={classes.navItem}>
                    <Typography className={classes.navItemText}>{item.title}</Typography>
                  </Button>
                </Link>
              )
            ))}
          </Box>

          {/* Desktop CTA Buttons */}
          <Box className={classes.navButtons}>
            <Button className={classes.navButton} onClick={handleLogin}>
              Log In
            </Button>
            <Button
              className={`${classes.navButton} ${classes.navButtonPrimary}`}
              onClick={handleForAcademies}
            >
              For Academies
            </Button>
          </Box>

          {/* Mobile Hamburger Menu */}
          <IconButton
            className={classes.hamburgerButton}
            onClick={handleMenuToggle}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile Sidebar Menu */}
      {mobileMenuOpen && (
        <Slide direction="left" in={mobileMenuOpen} mountOnEnter unmountOnExit timeout={500}>
          <Box className={classes.sidebarWrapper}>
            <Box className={classes.sidebar}>
              {/* Sidebar Header */}
              <Box className={classes.sidebarHeader}>
                <Box className={classes.sidebarLogoContainer}>
                  <Link to="/" onClick={handleCloseSidebar} style={{ textDecoration: 'none' }}>
                    <img src={logoBlack} alt="Languify Logo" className={classes.sidebarLogo} />
                  </Link>
                </Box>
                <CloseIcon onClick={handleCloseSidebar} className={classes.closeIcon} />
              </Box>

              {/* Sidebar Navigation Items */}
              <List className={classes.listContainer}>
                {/* Products Dropdown */}
                <Box className={classes.boxContainer}>
                  <ListItemButton
                    onClick={() => setOpenProducts(!openProducts)}
                    className={`${classes.listItem} ${openProducts ? classes.expandIcon : classes.normalExpandIcon}`}
                  >
                    <Typography variant="h6-semiBold">Products</Typography>
                    {openProducts ? <ExpandLess className={classes.expandIcon} /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={openProducts} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {productData.map((item, index) => (
                        <ListItemButton
                          key={index}
                          className={classes.listItemButton}
                          onClick={() => handleNavigate(item.link)}
                        >
                          <Typography variant="h6-medium">{item.label}</Typography>
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>

                {/* Use Case Dropdown */}
                <Box className={classes.boxContainer}>
                  <ListItemButton
                    onClick={() => setOpenUseCase(!openUseCase)}
                    className={`${classes.listItem} ${openUseCase ? classes.expandIcon : classes.normalExpandIcon}`}
                  >
                    <Typography variant="h6-semiBold">Solutions</Typography>
                    {openUseCase ? <ExpandLess className={classes.expandIcon} /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={openUseCase} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {useCaseData.map((item, index) => (
                        <ListItemButton
                          key={index}
                          className={classes.listItemButton}
                          onClick={() => handleNavigate(item.link)}
                        >
                          <Typography variant="h6-medium">{item.label}</Typography>
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>

                {/* Case Master Links */}
                {navLinks.map((item) => (
                  <Box className={classes.boxContainer} key={item.id}>
                    <ListItemButton
                      className={classes.listItem}
                      onClick={() => handleNavLink(item)}
                    >
                      <Typography variant="h6-semiBold">{item.label}</Typography>
                    </ListItemButton>
                  </Box>
                ))}

                {/* Regular Menu Items */}
                {menuItems.map((item, index) => (
                  <Box className={classes.boxContainer} key={index}>
                    <ListItemButton
                      className={classes.listItem}
                      onClick={() => handleNavigate(item.link, item.newPage)}
                    >
                      <Typography variant="h6-semiBold">{item.title}</Typography>
                    </ListItemButton>
                  </Box>
                ))}
              </List>

              {/* Sidebar CTA Buttons */}
              <Box className={classes.sidebarButtonsContainer}>
                <Button className={classes.sidebarButton} onClick={handleLogin}>
                  Log In
                </Button>
                <Button className={classes.sidebarButton} onClick={handleForAcademies}>
                  For Academies
                </Button>
              </Box>

            </Box>
          </Box>
        </Slide>
      )}
    </>
  );
};

export default CaseHeader;
